/* eslint-disable
no-mixed-operators, react/function-component-definition,
prefer-object-spread, camelcase,  no-plusplus, prefer-destructuring,
max-len */
import Jcampconverter from 'jcampconverter';
import { createSelector } from 'reselect';

import { FromManualToOffset } from './shift';
import Format from './format';
import { LIST_LAYOUT } from '../constants/list_layout';
import { getArea } from './integration';
import {
  inferLcMsCategory,
  buildLcmsMsPageJcamp,
  readLcmsMzPageFromJcampInfo,
  parseChemstationPages,
  isChemstationLcms,
} from '../features/lc-ms/parsing';

const canIntegrate = (layoutSt) => !(
  Format.isNmrLayout(layoutSt) || Format.isHplcUvVisLayout(layoutSt) || Format.isLCMsLayout(layoutSt)
);

const getTopic = (_, props) => props.topic;

const getFeature = (_, props) => props.feature;

const getLayout = (state, _) => state.layout; // eslint-disable-line

const GetCyclicVoltaShiftOffset = (volammetryData = null, curveIdx = 0) => {
  if (!volammetryData) return 0.0;
  const { spectraList } = volammetryData;
  const spectra = spectraList[curveIdx];
  if (!spectra) return 0.0;
  const { shift } = spectra;
  const { ref, val } = shift;
  if (!ref) return 0.0;
  const { e12 } = ref;
  return e12 - val;
};

const getShiftOffset = (state, _) => { // eslint-disable-line
  const {
    curve, layout, cyclicvolta,
  } = state;
  const { curveIdx } = curve;
  if (layout === LIST_LAYOUT.CYCLIC_VOLTAMMETRY && cyclicvolta) {
    return GetCyclicVoltaShiftOffset(cyclicvolta, curveIdx);
  }

  const { shift } = state;
  const { shifts } = shift;
  const selectedShift = shifts[curveIdx];
  if (!selectedShift) {
    return 0.0;
  }

  const { ref, peak } = selectedShift;
  return FromManualToOffset(ref, peak);
};

const calcXYK = (xs, ys, maxY, offset) => {
  const sp = [];
  let k = 0;
  for (let i = 0; i < ys.length; i += 1) { // no-downsample
    const x = xs[i] - offset;
    const y = ys[i];
    const cy = y / maxY;
    if (cy > 0.0) { k += cy; }
    sp.push({ x, y, k });
  }
  return sp;
};

const calcXY = (xs, ys, maxY, offset) => {
  const sp = [];
  for (let i = 0; i < ys.length; i += 1) { // no-downsample
    const x = xs[i] - offset;
    const y = ys[i];
    sp.push({ x, y });
  }
  return sp;
};

const convertTopic = (topic, layout, feature, offset) => {
  if (!feature || !topic) return [];
  const { maxY } = feature || {};
  const xs = topic.x || [];
  const ys = topic.y || [];

  const isItgDisable = canIntegrate(layout);
  if (!isItgDisable) return calcXYK(xs, ys, maxY, offset);
  return calcXY(xs, ys, maxY, offset);
};

const Topic2Seed = createSelector(
  getTopic,
  getLayout,
  getFeature,
  getShiftOffset,
  convertTopic,
);

const getOthers = (_, props) => props.comparisons;

const calcRescaleXY = (xs, ys, minY, maxY, show) => {
  const sp = [];
  if (xs.length < 1) return sp;
  const [lowerY, upperY] = [Math.min(...ys), Math.max(...ys)];
  const faktor = (maxY - minY) / (upperY - lowerY);
  for (let i = 0; i < ys.length; i += 2) { // downsample
    const x = xs[i];
    const y = (ys[i] - lowerY) * faktor + minY;
    sp.push({ x, y });
  }
  return { data: sp, show };
};

const convertComparisons = (layout, comparisons, feature) => {
  const { minY, maxY } = feature;
  if (!comparisons || !(Format.isIrLayout(layout)
  || Format.isHplcUvVisLayout(layout) || Format.isXRDLayout(layout))) return [];
  return comparisons.map((c) => {
    const { spectra, show } = c;
    const topic = spectra[0].data[0];
    const xs = topic.x;
    const ys = topic.y;
    return calcRescaleXY(xs, ys, minY, maxY, show);
  });
};

const GetComparisons = createSelector(
  getLayout,
  getOthers,
  getFeature,
  convertComparisons,
);

const convertFrequency = (layout, feature) => {
  if (!Format.isNmrLayout(layout)) return false;
  const { observeFrequency } = feature;
  const freq = Array.isArray(observeFrequency) ? observeFrequency[0] : observeFrequency;
  return parseFloat(freq) || false;
};

const ToFrequency = createSelector(
  getLayout,
  getFeature,
  convertFrequency,
);

const getThreshold = (state) => (
  state.threshold ? state.threshold.list[state.curve.curveIdx].value * 1.0 : false
);

const Convert2Peak = (feature, threshold, offset, upThreshold = false, lowThreshold = false) => {
  if (feature?.operation?.layout === 'LC/MS') {
    if (feature.peaks?.length) {
      return feature.peaks.map((p) => ({
        x: p.x - (offset || 0),
        y: p.y,
      }));
    }

    const data = feature.data[0];
    if (!data) return [];

    const { x, y } = data;
    const peaks = [];
    const maxIntensity = Math.max(...y);

    const thresholdValue = threshold || 0;
    for (let i = 1; i < y.length - 1; i++) {
      const intensity = (y[i] / maxIntensity) * 100;
      if (intensity >= thresholdValue && y[i] > y[i - 1] && y[i] > y[i + 1]) {
        peaks.push({
          x: x[i],
          y: y[i],
        });
      }
    }

    return peaks;
  }

  const peak = [];
  if (!feature || !feature.data) return peak;
  const data = feature.data[0];
  const {
    maxY, peakUp, thresRef, minY, upperThres, lowerThres, operation,
  } = feature;
  const { layout } = operation;

  if ((Format.isCyclicVoltaLayout(layout) || Format.isCDSLayout(layout))
  && (upperThres || lowerThres)) {
    let upperThresVal = upThreshold || upperThres;
    if (!upperThresVal) {
      upperThresVal = 1.0;
    }

    let lowerThresVal = lowThreshold || lowerThres;
    if (!lowerThresVal) {
      lowerThresVal = 1.0;
    }

    const yUpperThres = parseFloat(upperThresVal) / 100.0 * maxY;
    const yLowerThres = parseFloat(lowerThresVal) / 100.0 * minY;

    const corrOffset = offset || 0.0;
    for (let i = 0; i < data.y.length; i += 1) {
      const y = data.y[i];
      const overUpperThres = y >= yUpperThres;
      const belowThres = y <= yLowerThres;
      if (overUpperThres || belowThres) {
        const x = data.x[i] - corrOffset;
        peak.push({ x, y });
      }
    }
    return peak;
  }
  const thresVal = threshold || thresRef;
  const yThres = Number.parseFloat((thresVal * maxY / 100.0).toFixed(10));
  const corrOffset = offset || 0.0;
  for (let i = 0; i < data.y.length; i += 1) {
    const y = data.y[i];
    const overThres = (peakUp && Math.abs(y) >= yThres) || (!peakUp && Math.abs(y) <= yThres);
    if (overThres) {
      const x = data.x[i] - corrOffset;
      peak.push({ x, y });
    }
  }
  return peak;
};

const Feature2Peak = createSelector(
  getFeature,
  getThreshold,
  getShiftOffset,
  Convert2Peak,
);

const Convert2MaxMinPeak = (layout, feature, offset) => {  // eslint-disable-line
  const peaks = {
    max: [], min: [], pecker: [], refIndex: -1,
  };
  if (!Format.isCyclicVoltaLayout(layout) || !feature || !feature.data) return null;  // eslint-disable-line
  // const data = feature.data[0]; // eslint-disable-line
  const {
    volammetryData,
  } = feature;

  if (volammetryData && volammetryData.length > 0) {
    const maxArr = volammetryData.map((peakData) => {
      // peaks.refIndex = peakData.isRef === true ? idx : -1;
      if (peakData.max.x === '') return null;
      return { x: Number(peakData.max.x), y: Number(peakData.max.y) };
    });
    const minArr = volammetryData.map((peakData) => {
      if (peakData.min.x === '') return null;
      return { x: Number(peakData.min.x), y: Number(peakData.min.y) };
    });
    const peckerArr = volammetryData.map((peakData) => {
      if (peakData.pecker.x === '') return null;
      return { x: Number(peakData.pecker.x), y: Number(peakData.pecker.y) };
    });
    const refIndex = volammetryData.findIndex((peakData) => peakData.isRef === true);

    peaks.max = maxArr;
    peaks.min = minArr;
    peaks.pecker = peckerArr;
    peaks.refIndex = refIndex;
    return peaks;
  }

  return peaks;
};

const Feature2MaxMinPeak = createSelector(
  getLayout,
  getFeature,
  getShiftOffset,
  Convert2MaxMinPeak,
);

const convertThresEndPts = (feature, threshold) => {
  const {
    maxY, maxX, minX, thresRef,
  } = feature;

  const thresVal = threshold || thresRef || 0;
  if (!thresVal || !feature.data) return [];
  const yThres = thresVal * maxY / 100.0;
  const endPts = [{ x: minX - 200, y: yThres }, { x: maxX + 200, y: yThres }];
  return endPts;
};

const ToThresEndPts = createSelector(
  getFeature,
  getThreshold,
  convertThresEndPts,
);

const getShiftPeak = (state) => {
  const { curve, shift } = state;
  const { curveIdx } = curve;
  const { shifts } = shift;
  const selectedShift = shifts[curveIdx];
  if (!selectedShift) {
    return false;
  }
  return selectedShift.peak;
};

const convertSfPeaks = (peak, offset) => {
  if (!peak || !peak.x) return [];
  return [{ x: peak.x - offset, y: peak.y }];
};

const ToShiftPeaks = createSelector(
  getShiftPeak,
  getShiftOffset,
  convertSfPeaks,
);

// - - - - - - - - - - - - - - - - - - - - - -
// ExtractJcamp
// - - - - - - - - - - - - - - - - - - - - - -
const readLayout = (jcamp) => {
  if (jcamp.dataType?.toUpperCase?.() === 'LC/MS') {
    return LIST_LAYOUT.LC_MS;
  }
  const { xType, spectra } = jcamp;
  if (xType && Format.isNmrLayout(xType)) return xType;
  if (!spectra || !Array.isArray(spectra) || spectra.length === 0) {
    return LIST_LAYOUT.PLAIN;
  }
  const { dataType } = spectra[0] || {};
  if (dataType) {
    // Case-insensitive, like the backend's own comparison against data_type.json.
    const upperDataType = dataType.toUpperCase();
    // Single-crystal XRD is a reflection dataset, not a 1D diffractogram, and has no
    // layout of its own yet -- PLAIN, not the powder XRD layout below, whose substring
    // check it would otherwise match.
    if (upperDataType.includes('SINGLE CRYSTAL X-RAY DIFFRACTION')) {
      return LIST_LAYOUT.PLAIN;
    }
    if (upperDataType.includes('INFRARED SPECTRUM')) {
      return LIST_LAYOUT.IR;
    }
    if (upperDataType.includes('RAMAN SPECTRUM')) {
      return LIST_LAYOUT.RAMAN;
    }
    if (upperDataType.includes('UV/VIS SPECTRUM')) {
      if (upperDataType.includes('HPLC')) {
        return LIST_LAYOUT.HPLC_UVVIS;
      }
      return LIST_LAYOUT.UVVIS;
    }
    if (upperDataType.includes('THERMOGRAVIMETRIC ANALYSIS')) {
      return LIST_LAYOUT.TGA;
    }
    if (upperDataType.includes('DIFFERENTIAL SCANNING CALORIMETRY')) {
      return LIST_LAYOUT.DSC;
    }
    if (upperDataType.includes('X-RAY DIFFRACTION')) {
      return LIST_LAYOUT.XRD;
    }
    if (upperDataType.includes('MASS SPECTRUM')) {
      return LIST_LAYOUT.MS;
    }
    if (upperDataType.includes('CYCLIC VOLTAMMETRY')) {
      return LIST_LAYOUT.CYCLIC_VOLTAMMETRY;
    }
    if (upperDataType.includes('CIRCULAR DICHROISM SPECTROSCOPY')) {
      return LIST_LAYOUT.CDS;
    }
    if (upperDataType.includes('SIZE EXCLUSION CHROMATOGRAPHY')
      || upperDataType.includes('GEL PERMEATION CHROMATOGRAPHY')) {
      return LIST_LAYOUT.SEC;
    }
    if (upperDataType.includes('GAS CHROMATOGRAPHY')) {
      return LIST_LAYOUT.GC;
    }
    if (upperDataType.includes('SORPTION-DESORPTION MEASUREMENT')) {
      return LIST_LAYOUT.AIF;
    }
    if (upperDataType.includes('EMISSIONS')) {
      return LIST_LAYOUT.EMISSIONS;
    }
    if (upperDataType.includes('DLS ACF')) {
      return LIST_LAYOUT.DLS_ACF;
    }
    if (upperDataType.includes('DLS INTENSITY')) {
      return LIST_LAYOUT.DLS_INTENSITY;
    }
    if (upperDataType.includes('LC/MS')) {
      return LIST_LAYOUT.LC_MS;
    }
  }
  return LIST_LAYOUT.PLAIN;
};

const extrSpectraShare = (spectra, layout) => (
  spectra.map((s) => Object.assign({ layout }, s)).filter((r) => r != null)
);

const extrSpectraMs = (jcamp, layout) => {
  const csCategories = []
    .concat(jcamp?.info?.$CSCATEGORY || [])
    .map((c) => String(c).toUpperCase());

  const hasUvvisCategory = csCategories.some((c) => c.includes('UVVIS'));
  const hasUvvisDataType = jcamp?.spectra?.some((s) => {
    const dt = String(s?.dataType || '').toUpperCase();
    return dt.includes('UV-VIS') || dt.includes('UVVIS') || dt.includes('HPLC UV');
  });
  const jcampDataType = String(jcamp?.dataType || '').toUpperCase();
  const hasUvvisJcampDataType = jcampDataType.includes('UV-VIS') || jcampDataType.includes('UVVIS') || jcampDataType.includes('HPLC UV');
  const isUvvisData = hasUvvisCategory || hasUvvisDataType || hasUvvisJcampDataType;

  const hasTicCategory = csCategories.some((c) => c.includes('TIC'));
  const hasTicDataType = jcamp?.spectra?.some((s) => {
    const dt = String(s?.dataType || '').toUpperCase();
    return dt.includes('MASS TIC') || dt.includes('TIC');
  });
  const hasTicJcampDataType = jcampDataType.includes('MASS TIC') || jcampDataType.includes('TIC');
  const isTicData = hasTicCategory || hasTicDataType || hasTicJcampDataType;

  const getCategory = (idx) => csCategories[idx] || '';

  const finalSpectra = [];

  const parseIntegralsString = (raw) => {
    if (raw == null) return [];
    let text = raw;
    if (Array.isArray(text)) text = text.join(' ');
    text = String(text).trim();

    const groups = text.match(/\(([^)]+)\)/g) || [];
    const out = [];
    groups.forEach((g) => {
      const nums = g
        .replace(/[()]/g, '')
        .split(/[,\s;]+/)
        .map((s) => Number(s))
        .filter(Number.isFinite);
      if (nums.length >= 3) {
        const [xL, xU, area, absMaybe] = nums;
        out.push({
          xL,
          xU,
          area,
          absoluteArea: Number.isFinite(absMaybe) ? absMaybe : Math.abs(area),
          xExtent: { xL, xU },
        });
      }
    });
    return out;
  };

  const pickIntegralsForPair = (raw, idx) => {
    if (raw == null) return '';
    if (Array.isArray(raw)) return raw[idx] ?? '';
    if (typeof raw === 'string') return idx === 0 ? raw : '';
    return '';
  };

  // jcamp.info.UNITS is the whole ##UNITS= line for every variable (X, Y, and
  // often an implicit leading one — e.g. the NTUPLES page var — not listed in
  // ##SYMBOL= at all), so substring-scanning the concatenated line for
  // "SECOND"/"MINUTE" can match a Y-unit like "COUNTS PER SECOND" and
  // misclassify an unrelated X-axis. Resolve only the token ##SYMBOL= says
  // belongs to X, tolerating that common one-token UNITS/SYMBOL length
  // mismatch (units[0] is the unlabeled variable's unit, X starts at units[1]).
  const resolveJcampXUnit = (jcampInfo) => {
    const { UNITS, SYMBOL } = jcampInfo || {};
    if (!UNITS || !SYMBOL) return '';
    const unitsString = Array.isArray(UNITS) ? UNITS[0] : UNITS;
    const symbolString = Array.isArray(SYMBOL) ? SYMBOL[0] : SYMBOL;
    const units = String(unitsString).split(',').map((u) => u.trim());
    const symbols = String(symbolString).split(',').map((s) => s.trim().toUpperCase());
    const xIdx = symbols.indexOf('X');
    if (xIdx === -1) return '';
    const offset = units.length === symbols.length + 1 ? 1 : 0;
    return units[xIdx + offset] || '';
  };

  const jcampUnitsField = resolveJcampXUnit(jcamp?.info).toUpperCase();
  const jcampUnitsIndicatesMinutes = jcampUnitsField.includes('MINUTE');
  const jcampUnitsIndicatesSeconds = jcampUnitsField.includes('SECOND');

  const getMaxAbsX = (data) => {
    const xs = data?.[0]?.x;
    if (!Array.isArray(xs) || xs.length === 0) return 0;
    return xs.reduce((max, value) => {
      const abs = Math.abs(Number(value));
      return Number.isFinite(abs) && abs > max ? abs : max;
    }, 0);
  };

  // LC-MS time axes (UVVIS and TIC alike) are tagged MINUTES, an explicit
  // SECONDS, or an ambiguous "RETENTION TIME" that different exporters use for
  // either unit. Only convert when nothing explicitly says MINUTES, and either
  // SECONDS is explicit or the values themselves look like seconds (>60).
  //
  // The >60 magnitude fallback assumes no LC run runs longer than 60 minutes:
  // an ambiguously-tagged, untagged-units 90-minute run reads as seconds and
  // gets divided down to 1.5 minutes — wrong, but self-consistent, since this
  // PR applies the same rule to both the TIC and UVVIS panes. Widening this
  // threshold needs a real long-run fixture to weigh against short-run false
  // positives, not a speculative bump — see issue #619's review (S11).
  const resolveSecToMinScale = (spectrum) => {
    const xUnitUpper = String(spectrum?.xUnit || '').toUpperCase();
    const isExplicitMinutes = xUnitUpper.includes('MINUTE') || jcampUnitsIndicatesMinutes;
    const isExplicitSeconds = xUnitUpper.includes('SECOND') || jcampUnitsIndicatesSeconds;
    const isTimeAxis = xUnitUpper.includes('TIME') || xUnitUpper.includes('SECOND');
    const dataLooksLikeSeconds = getMaxAbsX(spectrum?.data) > 60;
    const needsSecToMin = isTimeAxis
      && !isExplicitMinutes
      && (isExplicitSeconds || dataLooksLikeSeconds);
    return needsSecToMin ? (1 / 60) : 1;
  };

  // An m/z page's ##PAGE=T=... retention-time marker has no xUnit of its own
  // (the spectrum's own xUnit is m/z) — it's expressed in whatever unit the
  // sibling TIC axis uses, with no unit tag on the value itself. Reuse the
  // same jcamp.info.UNITS override, and fall back to the value's own
  // magnitude — the same ">60 minutes never happens" assumption as
  // resolveSecToMinScale above, and the same caveat applies.
  const resolvePageMinuteScale = (pageValue) => {
    if (!Number.isFinite(pageValue)) return 1;
    if (jcampUnitsIndicatesMinutes) return 1;
    if (jcampUnitsIndicatesSeconds || Math.abs(pageValue) > 60) return 1 / 60;
    return 1;
  };

  // Scale every data block's x, not only data[0]: a spectrum can carry more
  // than one block, and rebuilding `data` as [data[0]] would silently drop the
  // rest along with the conversion.
  const scaleSpectrumDataX = (data, scale) => (data || []).map((block) => (
    Array.isArray(block?.x)
      ? Object.assign({}, block, { x: block.x.map((value) => value * scale) })
      : block
  ));

  if (isUvvisData) {
    const spectraList = jcamp.spectra || [];
    const uvvisSpectra = [];
    const peakTablesByPage = new Map();
    const hasPeakData = (table) => {
      const data = table?.data?.[0];
      if (!data) return false;
      if (Array.isArray(data)) return data.length >= 2;
      if (data?.x && data?.y) return data.x.length > 0 && data.y.length > 0;
      return false;
    };
    const buildPeaks = (source) => {
      if (!source) return [];
      if (Array.isArray(source)) {
        const peaks = [];
        for (let i = 0; i < source.length - 1; i += 2) {
          const x = Number(source[i]);
          const y = Number(source[i + 1]);
          if (Number.isFinite(x) && Number.isFinite(y)) peaks.push({ x, y });
        }
        return peaks;
      }
      if (source?.x && source?.y) {
        const len = Math.min(source.x.length, source.y.length);
        const peaks = new Array(len);
        for (let j = 0; j < len; j++) peaks[j] = { x: source.x[j], y: source.y[j] };
        return peaks;
      }
      return [];
    };

    spectraList.forEach((s, idx) => {
      if (!s) return;
      const sDataType = String(s.dataType || '').toUpperCase();
      const isUvvisSpectrum = (s.dataType === 'LC/MS' && getCategory(idx).includes('UVVIS'))
        || sDataType.includes('UV-VIS')
        || sDataType.includes('UVVIS')
        || sDataType.includes('HPLC UV');
      if (isUvvisSpectrum) {
        uvvisSpectra.push({ spectrum: s, idx });
      }
      if (s.dataType?.includes('PEAKTABLE')) {
        const pageKey = s.pageValue ?? s.page;
        if (pageKey == null) return;
        const entry = peakTablesByPage.get(pageKey) || {};
        const cat = getCategory(idx);
        if (cat.includes('AUTO_PEAK')) entry.auto = s;
        else if (cat.includes('EDIT_PEAK')) entry.edit = s;
        else entry.other = s;
        peakTablesByPage.set(pageKey, entry);
      }
    });

    const container = jcamp?.info?.$OBSERVEDINTEGRALS ?? null;

    uvvisSpectra.forEach(({ spectrum }, pairIdx) => {
      const minuteScale = resolveSecToMinScale(spectrum);
      const needsSecToMin = minuteScale !== 1;
      const scaleX = (value) => value * minuteScale;
      const pageKey = spectrum.pageValue ?? spectrum.page;
      const peakTable = peakTablesByPage.get(pageKey);
      let selectedPeakTable = null;
      if (hasPeakData(peakTable?.edit)) {
        selectedPeakTable = peakTable.edit;
      } else if (hasPeakData(peakTable?.auto)) {
        selectedPeakTable = peakTable.auto;
      } else if (hasPeakData(peakTable?.other)) {
        selectedPeakTable = peakTable.other;
      } else {
        selectedPeakTable = peakTable?.edit || peakTable?.auto || peakTable?.other || null;
      }

      const normalizedData = needsSecToMin
        ? scaleSpectrumDataX(spectrum.data, minuteScale)
        : spectrum.data;

      const mainSpectrum = {
        ...spectrum,
        data: normalizedData,
        peaks: [],
        integrations: [],
        csCategory: 'UVVIS PEAK TABLE',
      };

      const peakSource = selectedPeakTable?.data?.[0] || spectrum?.data?.[0];
      const peaks = buildPeaks(peakSource).map((p) => ({ ...p, x: scaleX(p.x) }));
      if (peaks.length) mainSpectrum.peaks = peaks;

      const rawText = pickIntegralsForPair(container, pairIdx);
      const integrals = parseIntegralsString(rawText).map((integ) => ({
        ...integ,
        xL: scaleX(integ.xL),
        xU: scaleX(integ.xU),
        xExtent: { xL: scaleX(integ.xL), xU: scaleX(integ.xU) },
      }));
      if (integrals.length) mainSpectrum.integrations = integrals;

      finalSpectra.push(mainSpectrum);
    });
  } else if (isTicData) {
    (jcamp.spectra || []).forEach((s) => {
      const hasPoints = s?.data?.[0]?.x?.length > 0;
      if (hasPoints) {
        const minuteScale = resolveSecToMinScale(s);
        const data = minuteScale === 1 ? s.data : scaleSpectrumDataX(s.data, minuteScale);
        finalSpectra.push({ ...s, data, csCategory: inferLcMsCategory(s, jcamp) });
      }
    });
  } else {
    // Only LC-MS group entities (m/z pages paired with a TIC/UVVIS sibling)
    // get their page value minute-normalized here — a standalone MS/GC-MS
    // layout's ##PAGE= can mean something else entirely (scan number,
    // precursor m/z, or a retention time already self-consistent within
    // that file), so leave it untouched outside the LC-MS group case.
    const isLcmsGroupLayout = layout === LIST_LAYOUT.LC_MS;
    (jcamp.spectra || []).forEach((s) => {
      const hasPoints = s?.data?.[0]?.x?.length > 0;
      if (hasPoints) {
        let spectrum = s;
        if (isLcmsGroupLayout) {
          const pageScale = resolvePageMinuteScale(s?.pageValue);
          if (pageScale !== 1) {
            spectrum = { ...s, pageValue: s.pageValue * pageScale };
          }
        }
        finalSpectra.push({ ...spectrum, csCategory: inferLcMsCategory(s, jcamp) });
      }
    });
  }

  let spectra = extrSpectraShare(finalSpectra, layout) || [];

  const info = jcamp?.info || {};
  if (info.UNITS && info.SYMBOL) {
    const unitsString = Array.isArray(info.UNITS) ? info.UNITS[0] : info.UNITS;
    const symbolString = Array.isArray(info.SYMBOL) ? info.SYMBOL[0] : info.SYMBOL;
    const units = String(unitsString).split(',');
    const symbols = String(symbolString).split(',');
    let xUnit = null;
    let yUnit = null;
    symbols.forEach((sym, idx) => {
      const curr = String(sym).replace(' ', '').toLowerCase();
      if (curr === 'x') xUnit = units[idx]?.trim?.() || null;
      if (curr === 'y') yUnit = units[idx]?.trim?.() || null;
    });
    spectra = spectra.map((sp) => {
      const spectrum = sp;
      if (xUnit) spectrum.xUnit = xUnit;
      if (yUnit) spectrum.yUnit = yUnit;
      return spectrum;
    });
  }
  return spectra;
};

const extrSpectraNi = (jcamp, layout) => {
  const categorys = jcamp.info.$CSCATEGORY || ['SPECTRUM'];
  const targetIdx = categorys.indexOf('SPECTRUM');
  const spectrum = extrSpectraShare(jcamp.spectra, layout)[targetIdx];
  return [spectrum] || [jcamp.spectra[0]];
};

const calcThresRef = (s, peakUp) => {
  if (!s || !s.data || !Array.isArray(s.data) || !s.data[0] || !s.data[0].y) {
    return null;
  }
  const ys = s.data[0].y;
  if (!ys || !Array.isArray(ys) || ys.length === 0) return null;
  const ref = peakUp ? Math.min(...ys.map((a) => Math.abs(a))) : Math.max(...ys);
  if (!s.maxY || s.maxY === 0) return null;
  return peakUp
    ? Math.floor(ref * 100 * 100 / s.maxY) / 100
    : Math.ceil(ref * 100 * 100 / s.maxY) / 100;
};

const calcUpperThres = (s) => {
  if (!s || !s.data || !Array.isArray(s.data) || !s.data[0] || !s.data[0].y) {
    return null;
  }
  const ys = s.data[0].y;
  if (!ys || !Array.isArray(ys) || ys.length === 0) return null;
  const ref = Math.max(...ys);
  if (!s.maxY || s.maxY === 0) return null;
  return Math.floor(ref * 100 * 100 / s.maxY) / 100;
};

const calcLowerThres = (s) => {
  if (!s || !s.data || !Array.isArray(s.data) || !s.data[0] || !s.data[0].y) {
    return null;
  }
  const ys = s.data[0].y;
  if (!ys || !Array.isArray(ys) || ys.length === 0) return null;
  const ref = Math.min(...ys);
  if (!s.minY || s.minY === 0) return null;
  return Math.ceil(ref * 100 * 100 / s.minY) / 100;
};

const extractShift = (s, jcamp) => {
  const shift = {
    selectX: false,
    solventName: false,
    solventValue: false,
  };
  if (!s) return shift;
  if (s && s.sampleDescription) {
    const desc = s.sampleDescription;
    const info = desc.split(/;|=/);

    return {
      selectX: parseFloat(info[1]),
      solventName: info[3],
      solventValue: parseFloat(info[5]),
    };
  }
  return {
    selectX: parseFloat(jcamp.info.$CSSOLVENTX) || false,
    solventName: jcamp.info.$CSSOLVENTNAME || false,
    solventValue: parseFloat(jcamp.info.$CSSOLVENTVALUE) || false,
  };
};

const extractVoltammetryData = (jcamp) => {
  const { info } = jcamp;
  if (!info.$CSCYCLICVOLTAMMETRYDATA) return null;
  const regx = /[^0-9.,E,e,-]/g;
  const rawData = info.$CSCYCLICVOLTAMMETRYDATA.split('\n');
  const peakStack = rawData.map((line) => {
    const splittedLine = line.replace(regx, '').split(',');
    const isRef = (splittedLine.length > 8 && splittedLine[8] === '1');
    return {
      max: { x: splittedLine[0], y: splittedLine[1] },
      min: { x: splittedLine[2], y: splittedLine[3] },
      ratio: splittedLine[4],
      delta: splittedLine[5],
      pecker: { x: splittedLine[6], y: splittedLine[7] },
      isRef,
    };
  });
  return peakStack;
};

// A `##$CS...=true` record; one written in several blocks is parsed into an array.
const isTrueRecord = (value) => [].concat(value ?? [])
  .some((v) => String(v).trim().toLowerCase() === 'true');

const buildPeakFeature = (jcamp, layout, peakUp, s, thresRef, upperThres = false, lowerThres = false) => {
  const { xType, info } = jcamp;
  const subTyp = xType ? ` - ${xType}` : '';

  const baseFeature = {
    typ: s.dataType + subTyp,
    peakUp,
    thresRef,
    scanCount: +info.$CSSCANCOUNT,
    scanAutoTarget: +info.$CSSCANAUTOTARGET,
    scanEditTarget: +info.$CSSCANEDITTARGET,
    shift: extractShift(s, jcamp),
    operation: {
      layout,
      nucleus: xType || '',
    },
    observeFrequency: info['.OBSERVEFREQUENCY'],
    solventName: info['.SOLVENTNAME'],
    upperThres,
    lowerThres,
    volammetryData: extractVoltammetryData(jcamp),
    scanRate: +info.$CSSCANRATE || 0.1,
    weAreaValue: info.$CSWEAREAVALUE || '',
    weAreaUnit: info.$CSWEAREAUNIT || '',
    currentMode: (info.$CSCURRENTMODE || ''),
    csCategory: info.$CSCATEGORY || s.csCategory,
    // What a client asked chem-spectra-app to do with the y signal; written only when
    // asked. $CSTRANSMITTANCE: absorbance was converted to %T -- a file that was %T to
    // begin with has no record. $CSINVERTY: draw y inverted; the stored data is left
    // untouched from chem-spectra-app#304 (#298 mirrored it instead).
    convertedToTransmittance: isTrueRecord(info.$CSTRANSMITTANCE),
    invertedY: isTrueRecord(info.$CSINVERTY),
  };

  if (layout === 'LC/MS') {
    if (s.peaks) baseFeature.peaks = s.peaks;
    if (s.integrations) baseFeature.integrations = s.integrations;
  }

  return Object.assign({}, baseFeature, s);
};

const maxArray = (arr) => {
  let len = arr.length;
  let max = -Infinity;

  while (len--) {
    max = arr[len] > max ? arr[len] : max;
  }
  return max;
};

const calcIntgRefArea = (spectra, stack) => {
  if (stack.length === 0) return 1;
  const data = spectra[0].data[0];

  const xs = data.x;
  const ys = data.y;
  const maxY = maxArray(ys);

  const xyk = calcXYK(xs, ys, maxY, 0);
  const { xL, xU, area } = stack[0];
  const rawArea = getArea(xL, xU, xyk);
  const raw2realRatio = rawArea / area;
  return { raw2realRatio };
};

const parseObservedIntegralGroups = (rawValue) => {
  if (!rawValue) return {};
  const tokenRegx = /[^A-Za-z0-9._-]/g;
  const groupsByIdx = {};
  rawValue.split('\n').forEach((line) => {
    const cells = line.split(',').map((c) => c.replace(tokenRegx, ''));
    if (cells.length < 2) return;
    const idx = parseInt(cells[0], 10);
    const groupId = cells[1];
    if (!Number.isInteger(idx) || idx < 0 || !groupId) return;
    groupsByIdx[idx] = groupId;
  });
  return groupsByIdx;
};

const buildIntegFeature = (jcamp, spectra) => {
  const {
    $OBSERVEDINTEGRALS, $OBSERVEDMULTIPLETS, $OBSERVEDINTEGRALSGROUPS,
  } = jcamp.info;
  const regx = /[^0-9.,-]/g;
  let stack = [];
  if ($OBSERVEDINTEGRALS) {
    const its = $OBSERVEDINTEGRALS.split('\n').slice(1);
    const groupsByIdx = parseObservedIntegralGroups($OBSERVEDINTEGRALSGROUPS);
    const itStack = its.map((t, idx) => {
      const ts = t.replace(regx, '').split(',');
      const item = {
        xL: parseFloat(ts[0]),
        xU: parseFloat(ts[1]),
        area: parseFloat(ts[2]),
        absoluteArea: parseFloat(ts[3]),
      };
      const groupId = groupsByIdx[idx];
      return groupId
        ? Object.assign({}, item, { visualSplitGroupId: groupId })
        : item;
    });
    stack = [...stack, ...itStack];
  }
  if ($OBSERVEDMULTIPLETS) {
    const mps = $OBSERVEDMULTIPLETS.split('\n');
    const mpStack = mps.map((m) => {
      const ms = m.replace(regx, '').split(',');
      return {
        xL: parseFloat(ms[1]),
        xU: parseFloat(ms[2]),
        area: parseFloat(ms[4]),
      };
    });
    stack = [...stack, ...mpStack];
  }
  const { raw2realRatio } = calcIntgRefArea(spectra, stack);
  const mStack = stack.map((st) => Object.assign({}, st, { area: st.area * raw2realRatio }));

  return (
    {
      refArea: raw2realRatio,
      refFactor: 1,
      shift: 0,
      stack: mStack,
      originStack: stack,
    }
  );
};

/*
const range = (head, tail, length) => {
  const actTail = tail || length - 1;
  return (
    Array(actTail - head + 1).fill().map((_, idx) => head + idx)
  );
};
*/

const buildSimFeature = (jcamp) => {
  const { $CSSIMULATIONPEAKS } = jcamp.info;
  let nmrSimPeaks = $CSSIMULATIONPEAKS ? $CSSIMULATIONPEAKS.split('\n') : [];
  nmrSimPeaks = nmrSimPeaks.map((x) => parseFloat(x).toFixed(2));
  return {
    nmrSimPeaks,
  };
};

const buildMpyFeature = (jcamp) => {
  const { $OBSERVEDMULTIPLETS, $OBSERVEDMULTIPLETSPEAKS } = jcamp.info;
  const regx = /[^A-Za-z0-9.,-]/g;
  const regxNum = /[^0-9.]/g;
  let stack = [];
  if (!$OBSERVEDMULTIPLETSPEAKS) return { stack: [] };
  const allPeaks = $OBSERVEDMULTIPLETSPEAKS.split('\n').map(
    (p) => p.replace(regx, '').split(','),
  );

  if ($OBSERVEDMULTIPLETS) {
    const mp = $OBSERVEDMULTIPLETS.split('\n');
    const mpStack = mp.map((m) => {
      const ms = m.replace(regx, '').split(',');
      const idx = ms[0];
      let ys = [];
      const peaks = allPeaks.map((p) => {
        if (p[0] === idx) {
          ys = [...ys, parseFloat(p[2])];
          return { x: parseFloat(p[1]), y: parseFloat(p[2]) };
        }
        return null;
      }).filter((r) => r != null);
      let js = m.split(',');
      js = js[js.length - 1].split(' ')
        .map((j) => parseFloat(j.replace(regxNum, '')))
        .filter(Boolean);

      return {
        js,
        mpyType: ms[6],
        xExtent: {
          xL: parseFloat(ms[1]),
          xU: parseFloat(ms[2]),
        },
        yExtent: {
          yL: Math.min(...ys),
          yU: Math.max(...ys),
        },
        peaks,
      };
    });
    stack = [...stack, ...mpStack];
  }

  return (
    {
      stack,
      shift: 0,
      smExtext: false,
    }
  );
};

const isPeakTable = (s) => (
  s.dataType && (
    s.dataType.includes('PEAKTABLE')
      || s.dataType.includes('PEAK ASSIGNMENTS')
  )
);

const extrFeaturesNi = (jcamp, layout, peakUp, spectra) => {
  const nfs = {};
  const category = jcamp.info.$CSCATEGORY;
  if (category) {
    const idxEditPeak = category.indexOf('EDIT_PEAK');
    if (idxEditPeak >= 0) {
      const sEP = jcamp.spectra[idxEditPeak];
      const thresRef = calcThresRef(sEP, peakUp);
      nfs.editPeak = buildPeakFeature(jcamp, layout, peakUp, sEP, thresRef);
    }
    const idxAutoPeak = category.indexOf('AUTO_PEAK');
    if (idxAutoPeak >= 0) {
      const sAP = jcamp.spectra[idxAutoPeak];
      const thresRef = calcThresRef(sAP, peakUp);
      nfs.autoPeak = buildPeakFeature(jcamp, layout, peakUp, sAP, thresRef);
    }
    nfs.integration = buildIntegFeature(jcamp, spectra);
    nfs.multiplicity = buildMpyFeature(jcamp);
    nfs.simulation = buildSimFeature(jcamp);
    return nfs;
  }

  // workaround for legacy design
  const features = jcamp.spectra.map((s) => {
    const thresRef = calcThresRef(s, peakUp);
    return isPeakTable(s)
      ? buildPeakFeature(jcamp, layout, peakUp, s, thresRef)
      : null;
  }).filter((r) => r != null);

  const integration = buildIntegFeature(jcamp, spectra);
  const multiplicity = buildMpyFeature(jcamp);
  const simulation = buildSimFeature(jcamp);

  return {
    editPeak: features[0], autoPeak: features[1], integration, multiplicity, simulation,
  };
};

const getBoundary = (s) => {
  const { x, y } = s.data[0];
  // Math.max/min of an empty array is -Infinity/+Infinity: an empty (0-point)
  // peak-table block must not give its feature infinite bounds.
  if (!x?.length || !y?.length) {
    return {
      maxX: 0, minX: 0, maxY: 0, minY: 0,
    };
  }
  const maxX = Math.max(...x);
  const minX = Math.min(...x);
  const maxY = Math.max(...y);
  const minY = Math.min(...y);
  return {
    maxX, minX, maxY, minY,
  };
};

const extrFeaturesXrd = (jcamp, layout, peakUp) => {
  const base = jcamp.spectra[0];

  const features = jcamp.spectra.map((s) => {
    const upperThres = Format.isXRDLayout(layout) ? 100 : calcUpperThres(s);
    const lowerThres = Format.isXRDLayout(layout) ? 100 : calcLowerThres(s);
    const cpo = buildPeakFeature(jcamp, layout, peakUp, s, 100, upperThres, lowerThres);
    const bnd = getBoundary(s);
    return Object.assign({}, base, cpo, bnd);
  }).filter((r) => r != null);

  const category = jcamp.info.$CSCATEGORY;
  if (category) {
    const idxEditPeak = category.indexOf('EDIT_PEAK');
    if (idxEditPeak >= 0) {
      const sEP = jcamp.spectra[idxEditPeak];
      const thresRef = calcThresRef(sEP, peakUp);
      features.editPeak = buildPeakFeature(jcamp, layout, peakUp, sEP, thresRef);
    }
    const idxAutoPeak = category.indexOf('AUTO_PEAK');
    if (idxAutoPeak >= 0) {
      const sAP = jcamp.spectra[idxAutoPeak];
      const thresRef = calcThresRef(sAP, peakUp);
      features.autoPeak = buildPeakFeature(jcamp, layout, peakUp, sAP, thresRef);
    }
  }

  return features;
};

const extrFeaturesCylicVolta = (jcamp, layout, peakUp) => {
  const base = jcamp.spectra[0];

  const features = jcamp.spectra.map((s) => {
    const upperThres = Format.isXRDLayout(layout) ? 100 : calcUpperThres(s);
    const lowerThres = Format.isXRDLayout(layout) ? 100 : calcLowerThres(s);
    const cpo = buildPeakFeature(jcamp, layout, peakUp, s, 100, upperThres, lowerThres);
    const bnd = getBoundary(s);
    let detector = '';
    let secData = null;
    if (Format.isSECLayout(layout)) {
      const { info } = jcamp;
      detector = info.$DETECTOR ? info.$DETECTOR : '';
      const {
        D, MN, MP, MW,
      } = info;
      secData = {
        d: D, mn: MN, mp: MP, mw: MW,
      };
    }
    return Object.assign({}, base, cpo, bnd, { detector, secData });
  }).filter((r) => r != null);

  return features;
};

const extrFeaturesMs = (jcamp, layout, peakUp, spectra) => {
  const thresRef = (jcamp.info && jcamp.info.$CSTHRESHOLD * 100) || 5;
  const features = spectra.map((s) => {
    if (!s.data || !s.data[0] || !s.data[0].x || !s.data[0].y) {
      return null;
    }
    const cpo = buildPeakFeature(jcamp, layout, peakUp, s, +thresRef.toFixed(4));
    const bnd = getBoundary(s);
    return Object.assign({}, cpo, bnd);
  }).filter((r) => r != null);
  return features;
};

const extractTemperature = (jcamp) => {
  if ('$CSAUTOMETADATA' in jcamp.info) {
    const match = jcamp.info.$CSAUTOMETADATA.match(/TEMPERATURE=([\d.]+)/);
    if (match !== null) {
      const temperature = match[1];
      return temperature;
    }
  }
  return 'xxx';
};

const normalizeXyData = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return null;
    const first = raw[0];
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const x = Array.isArray(first.x) ? first.x : [];
      const y = Array.isArray(first.y) ? first.y : [];
      if (x.length || y.length) return { x, y };
    }
    if (Array.isArray(first)) {
      if (raw.length === 2 && Array.isArray(raw[0]) && Array.isArray(raw[1])) {
        return { x: raw[0], y: raw[1] };
      }
      const x = [];
      const y = [];
      raw.forEach((pair) => {
        if (!Array.isArray(pair) || pair.length < 2) return;
        const xVal = Number(pair[0]);
        const yVal = Number(pair[1]);
        if (Number.isFinite(xVal) && Number.isFinite(yVal)) {
          x.push(xVal);
          y.push(yVal);
        }
      });
      if (x.length || y.length) return { x, y };
    }
  } else if (typeof raw === 'object') {
    const x = Array.isArray(raw.x) ? raw.x : [];
    const y = Array.isArray(raw.y) ? raw.y : [];
    if (x.length || y.length) return { x, y };
  }
  return null;
};

const ensureSpectrumData = (spectrum, source) => {
  if (!spectrum) return spectrum;
  const result = { ...spectrum };
  let normalized = normalizeXyData(result.data);
  if (!normalized && source) {
    normalized = normalizeXyData({ x: source.x, y: source.y }) || normalizeXyData(source.data);
  }
  if (normalized) {
    result.data = [{ x: normalized.x || [], y: normalized.y || [] }];
  }
  return result;
};

const ExtractJcamp = (source) => {
  const jcamp = Jcampconverter.convert(
    source,
    {
      xy: true,
      keepRecordsRegExp: /(\$CSTHRESHOLD|\$CSSCANAUTOTARGET|\$CSSCANEDITTARGET|\$CSSCANCOUNT|\$CSSOLVENTNAME|\$CSSOLVENTVALUE|\$CSSOLVENTX|\$CSCATEGORY|\$CSITAREA|\$CSITFACTOR|\$OBSERVEDINTEGRALS|\$OBSERVEDINTEGRALSGROUPS|\$OBSERVEDMULTIPLETS|\$OBSERVEDMULTIPLETSPEAKS|\.SOLVENTNAME|\.OBSERVEFREQUENCY|\$CSSIMULATIONPEAKS|\$CSUPPERTHRESHOLD|\$CSLOWERTHRESHOLD|\$CSCYCLICVOLTAMMETRYDATA|UNITS|SYMBOL|\$CSAUTOMETADATA|\$DETECTOR|MN|MW|D|MP|MELTINGPOINT|TG|\$CSSCANRATE|\$CSSPECTRUMDIRECTION|\$CSTRANSMITTANCE|\$CSINVERTY|\$CSWEAREAVALUE|\$CSWEAREAUNIT|\$CSCURRENTMODE|\$CSLCMSMZPAGE|SCAN_MODE|SCANMODE|VAR_TYPE|VARTYPE|TYPE|SOFTWARE|DATATYPE)/, // eslint-disable-line
    },
  );
  const isChemstation = isChemstationLcms(source, jcamp);
  const parsedPages = parseChemstationPages(source, jcamp);
  const spectraCount = Array.isArray(jcamp.spectra) ? jcamp.spectra.length : 0;
  if (parsedPages.length > 1 && spectraCount < parsedPages.length) {
    jcamp.spectra = parsedPages;
  }

  const hasNtuples = jcamp.ntuples && Array.isArray(jcamp.ntuples) && jcamp.ntuples.length > 0;
  const hasSpectra = jcamp.spectra && Array.isArray(jcamp.spectra) && jcamp.spectra.length > 0;
  const hasNtupleData = hasNtuples && jcamp.ntuples.some((ntuple) => (
    !!normalizeXyData(ntuple?.data) || !!normalizeXyData({ x: ntuple?.x, y: ntuple?.y })
  ));
  const hasSpectraData = hasSpectra && jcamp.spectra.some((spectrum) => (
    !!normalizeXyData(spectrum?.data) || !!normalizeXyData({ x: spectrum?.x, y: spectrum?.y })
  ));

  if (hasNtuples && hasNtupleData) {
    if (hasSpectra && jcamp.spectra.length === 1 && jcamp.ntuples.length > 1) {
      const singleSpectrum = jcamp.spectra[0];
      jcamp.spectra = jcamp.ntuples.map((ntuple) => {
        const spectrum = {
          ...singleSpectrum,
          ...ntuple,
          dataType: singleSpectrum.dataType || jcamp.dataType || ntuple.dataType,
          xUnit: ntuple.xUnit || singleSpectrum.xUnit || jcamp.info?.XUNITS,
          yUnit: ntuple.yUnit || singleSpectrum.yUnit || jcamp.info?.YUNITS,
          pageValue: ntuple.pageValue || ntuple.page,
          page: ntuple.page || ntuple.pageValue,
          pageSymbol: ntuple.pageSymbol || ntuple.pageValue || ntuple.page,
        };
        ensureSpectrumData(spectrum, ntuple);
        return spectrum;
      });
    } else if (!hasSpectra || !hasSpectraData || jcamp.spectra.length < jcamp.ntuples.length) {
      jcamp.spectra = jcamp.ntuples.map((ntuple) => {
        const spectrum = {
          ...ntuple,
          dataType: jcamp.dataType || ntuple.dataType,
          xUnit: ntuple.xUnit || jcamp.info?.XUNITS,
          yUnit: ntuple.yUnit || jcamp.info?.YUNITS,
          pageValue: ntuple.pageValue || ntuple.page,
          page: ntuple.page || ntuple.pageValue,
          pageSymbol: ntuple.pageSymbol || ntuple.pageValue || ntuple.page,
        };
        ensureSpectrumData(spectrum, ntuple);
        return spectrum;
      });
    }
  }

  let layout = readLayout(jcamp);
  if (isChemstation) {
    layout = LIST_LAYOUT.LC_MS;
  }
  const peakUp = !Format.isIrLayout(layout);

  const spectra = (Format.isMsLayout(layout) || Format.isLCMsLayout(layout))
    ? extrSpectraMs(jcamp, layout)
    : extrSpectraNi(jcamp, layout);

  let features = {};
  if (Format.isMsLayout(layout) || Format.isLCMsLayout(layout)) {
    features = extrFeaturesMs(jcamp, layout, peakUp, spectra);
  } else if (Format.isXRDLayout(layout)) {
    features = extrFeaturesXrd(jcamp, layout, peakUp);
    const temperature = extractTemperature(jcamp);
    return {
      spectra, features, layout, temperature,
    };
  } else if (Format.isCyclicVoltaLayout(layout) || Format.isSECLayout(layout)
  || Format.isAIFLayout(layout) || Format.isCDSLayout(layout) || Format.isGCLayout(layout)) {
    features = extrFeaturesCylicVolta(jcamp, layout, peakUp);
  } else {
    features = extrFeaturesNi(jcamp, layout, peakUp, spectra);
    if (Format.isDSCLayout(layout)) {
      const { info } = jcamp;
      const {
        MELTINGPOINT, TG,
      } = info;
      const dscMetaData = {
        meltingPoint: MELTINGPOINT,
        tg: TG,
      };
      features = Object.assign({}, features, { dscMetaData });
    }
  }
  // const features = Format.isMsLayout(layout)
  //   ? extrFeaturesMs(jcamp, layout, peakUp)
  //   : ((Format.isXRDLayout(layout) || Format.isCyclicVoltaLayout(layout))
  //     ? extrFeaturesXrd(jcamp, layout, peakUp) : extrFeaturesNi(jcamp, layout, peakUp, spectra));

  const lcmsMzPageFromInfo = Format.isLCMsLayout(layout)
    ? readLcmsMzPageFromJcampInfo(jcamp.info)
    : null;
  if (lcmsMzPageFromInfo != null) {
    return {
      spectra,
      features,
      layout,
      lcms_mz_page: lcmsMzPageFromInfo,
    };
  }
  return { spectra, features, layout };
};

const Convert2Scan = (feature, scanSt) => {
  const { scanAutoTarget, scanEditTarget } = feature;
  const { target, isAuto } = scanSt;
  const hasEdit = !!scanEditTarget;
  const defaultIdx = (isAuto || !hasEdit) ? scanAutoTarget : scanEditTarget;
  return target || defaultIdx;
};

const Convert2Thres = (feature, thresSt) => {
  const value = thresSt.value || feature.thresRef;
  return value;
};

const Convert2DValue = (doubleTheta, lambda = 0.15406, isRadian = true) => {
  let theta = doubleTheta / 2;
  if (isRadian) {
    theta = (theta / 180) * Math.PI;
  }
  const sinTheta = Math.sin(theta);
  const dValue = lambda / (2 * sinTheta);
  return dValue;
};

const GetCyclicVoltaRatio = (y_max_peak, y_min_peak, y_pecker) => {
  const firstExpr = Math.abs(y_min_peak) / Math.abs(y_max_peak);
  const secondExpr = 0.485 * Math.abs(y_pecker) / Math.abs(y_max_peak);
  const ratio = firstExpr + secondExpr + 0.086;
  return ratio;
};

const GetCyclicVoltaPeakSeparate = (x_max_peak, x_min_peak) => {
  const delta = Math.abs(x_max_peak - x_min_peak);
  return delta;
};

const GetCyclicVoltaPreviousShift = (cyclicVolta, curveIdx) => {
  if (!cyclicVolta) {
    return 0.0;
  }
  const { spectraList } = cyclicVolta;
  if (spectraList.length <= curveIdx) {
    return 0.0;
  }
  const { shift, hasRefPeak } = spectraList[curveIdx];
  const { prevValue } = shift;
  return hasRefPeak ? prevValue : -prevValue;
};

export {
  ExtractJcamp, Topic2Seed, Feature2Peak,
  ToThresEndPts, ToShiftPeaks, ToFrequency,
  Convert2Peak, Convert2Scan, Convert2Thres,
  GetComparisons, Convert2DValue,
  GetCyclicVoltaRatio, GetCyclicVoltaPeakSeparate,
  Feature2MaxMinPeak, convertTopic, Convert2MaxMinPeak,
  GetCyclicVoltaShiftOffset, GetCyclicVoltaPreviousShift,
  convertThresEndPts, buildLcmsMsPageJcamp,
  buildIntegFeature,
};
